import type { ReconcileTarget } from './discoveryGridApi';

/**
 * When a new SOW (= engagement) is added to a company that already has one,
 * the Add SOW flow offers the scope-reconciliation step against the previous
 * engagement's leftovers (docs/DISCOVERY_GRID_REDESIGN_SPEC.md §4.10; build
 * decision 26 is superseded). Pure helpers; the parents own the state.
 */

export interface EngagementLite {
  id: string;
  name?: string;
  title?: string;
  status?: string;
  createdAt?: string;
}

export interface ReconcileOffer {
  target: ReconcileTarget;
  /** Preselected source engagement */
  fromEngagementId: string;
  /** Candidate sources (every engagement of the company but the new one) */
  engagements: EngagementLite[];
}

function createdMs(e: EngagementLite): number {
  const t = e.createdAt ? new Date(e.createdAt).getTime() : NaN;
  return Number.isFinite(t) ? t : -Infinity;
}

/**
 * The engagement a new one most plausibly continues: the latest created among
 * the others; with no dates, the first in the list. Null when the company had
 * no other engagement.
 */
export function pickPreviousEngagement(engagements: EngagementLite[], newEngagementId: string): EngagementLite | null {
  const others = engagements.filter((e) => e && e.id && e.id !== newEngagementId);
  if (others.length === 0) return null;
  return others.reduce((best, e) => (createdMs(e) > createdMs(best) ? e : best), others[0]);
}

/** What to open after Add SOW closes, or null when there is nothing to reconcile against. */
export function reconcileOfferFor(created: { engagementId: string } | null | undefined, engagements: EngagementLite[]): ReconcileOffer | null {
  if (!created?.engagementId) return null;
  const previous = pickPreviousEngagement(engagements, created.engagementId);
  if (!previous) return null;
  const target = engagements.find((e) => e.id === created.engagementId);
  return {
    target: { type: 'engagement', engagementId: created.engagementId, label: target?.name || target?.title || 'the new SOW' },
    fromEngagementId: previous.id,
    engagements: engagements.filter((e) => e.id !== created.engagementId),
  };
}

export const RECONCILE_AFTER_SOW_INTRO =
  'This company already had an engagement. Match what was left unfinished or parked there against the new SOW: carried blocks keep their answers and position; "leave" changes nothing. Skip now and use "Carry forward from a previous engagement…" on the grid later.';
